import { useState, useEffect, useCallback } from 'react';
import {
  onUpdateAvailable,
  onInstallPromptAvailable,
  applyUpdate,
  promptInstall,
  canInstall,
  isStandalone,
} from '@/lib/pwa';

interface UsePWAResult {
  updateAvailable: boolean;
  applyUpdate: () => Promise<void>;
  canInstall: boolean;
  promptInstall: () => Promise<boolean>;
  isStandalone: boolean;
}

export function usePWA(): UsePWAResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [installAvailable, setInstallAvailable] = useState(canInstall());
  const [standalone] = useState(isStandalone());

  useEffect(() => {
    const unsubUpdate = onUpdateAvailable(setUpdateAvailable);
    const unsubInstall = onInstallPromptAvailable((event) => {
      setInstallAvailable(event !== null);
    });

    return () => {
      unsubUpdate();
      unsubInstall();
    };
  }, []);

  const handleApplyUpdate = useCallback(async () => {
    await applyUpdate();
  }, []);

  const handlePromptInstall = useCallback(async () => {
    const accepted = await promptInstall();
    if (accepted) setInstallAvailable(false);
    return accepted;
  }, []);

  return {
    updateAvailable,
    applyUpdate: handleApplyUpdate,
    canInstall: installAvailable,
    promptInstall: handlePromptInstall,
    isStandalone: standalone,
  };
}
